import type { AxiosResponse } from 'axios'

import paperReportingQueries from '@/api/modules/paperReportingQueries'
import productionSitesQueries from '@/api/modules/productionSitesQueries'
import reportExecutionQueries from '@/api/modules/reportExecutionQueries'
import reportMaterialsQueries from '@/api/modules/reportMaterialsQueries'
import reportPlateOrdersQueries from '@/api/modules/reportPlateOrdersQueries'
import reportTaskDiscoveryQueries from '@/api/modules/reportTaskDiscoveryQueries'
import type { ProductionSiteDetails, ProductionSiteMachine } from '@/api/schemas/productionSites/productionSites'
import type { ActiveReportDetail, ReportWorkspace } from '@/api/schemas/reports/common'
import type {
    AdditionalActionsResponseSchema,
    NextPlatesToReportResponseSchema,
    NextPlateToReportResponseSchema,
    ReportCommentCodesResponseSchema,
    ReportTaskDetailsResponseSchema
} from '@/api/schemas/reports/operatorReporting'
import type {
    AddReportPalletPayloadSchema,
    AddReportPalletResponseSchema,
    CheckPalletParameterResponseSchema
} from '@/api/schemas/reports/paperReporting'
import type {
    CreatePlateOrderPayload,
    MachinePlateItemsResponseSchema,
    PlateOrderHistoryParams,
    PlateOrderHistoryResponseSchema,
    PlateOrderReasonsResponseSchema
} from '@/api/schemas/reports/plateOrders'
import type {
    AddCodeCompliancePatternPayloadSchema,
    AddCodeCompliancePatternResponseSchema,
    BlackBoxAmountPayloadSchema,
    BlackBoxAmountResponseSchema,
    BlackBoxCounterSwitchPayloadSchema,
    BlackBoxCounterSwitchResponseSchema,
    BlackBoxLightPayloadSchema,
    BreakReportPayloadSchema,
    CodeCompliancesResponseSchema,
    CreateCodeCompliancePayloadSchema,
    CreateCodeComplianceResponseSchema,
    CreateProcessReportPayloadSchema,
    CreateReportPalletData,
    CreateReportPayloadSchema,
    CreateReportRequestPayloadSchema,
    CreateReportResponseSchema,
    EndMakeReadyPayloadSchema,
    FinishReportPayloadSchema,
    PrinectAmountPayloadSchema,
    PrinectAmountResponseSchema,
    PrinectMakeReadyAmountResponseSchema,
    RenewReportPayloadSchema,
    ReportAdditionalAction,
    ReportDetailResponseSchema,
    ReportItemColoursResponseSchema,
    ReportTaskDetails,
    ReportTaskPlate,
    ReportTaskPlateSideType,
    UpdateReportPayloadSchema,
    UpdateReportTaskPlatePayloadSchema,
    UpdateReportTaskPlateResponseSchema
} from '@/api/schemas/reports/reportExecution'
import type {
    ReportMaterial,
    ReportMaterialApi,
    ReportMaterialReservationPayloadSchema,
    ReportMaterialsResponseSchema,
    ReportMaterialTypesResponseSchema
} from '@/api/schemas/reports/reportMaterials'
import { REPORTS } from '@/constants'
import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'
import type { OperatorReportingCreateContext } from '@/stores/reports/reportExecution/types'
import { handleApiError } from '@/utils/apiHelpers'
import { buildRuntimeAdditionalActionsRequest } from '@/views/Reports/ReportExecution/ActiveReport/helpers/runtime'

interface CreateReportPayloadInput {
    task: ReportTaskDetails
    plate: ReportTaskPlate
    context: OperatorReportingCreateContext
    workspace: Pick<ReportWorkspace, 'id' | 'makeReadyRequired'> & {
        machine: Pick<ProductionSiteMachine, 'id'> | null
    }
    userId: number | null
    additionalActions: ReportAdditionalAction[]
    reportPalletsData: CreateReportPalletData[]
}

class ReportExecutionService {
    async fetchReport(reportId: number | string): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        try {
            return await reportExecutionQueries.getReport(reportId)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchReportError'))
            throw error
        }
    }

    async fetchProductionSite(productionSiteId: number | string): Promise<AxiosResponse<ProductionSiteDetails>> {
        try {
            return await productionSitesQueries.fetchProductionSite(productionSiteId)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchProductionSiteError'))
            throw error
        }
    }

    async fetchTaskDetails(context: OperatorReportingCreateContext): Promise<AxiosResponse<ReportTaskDetailsResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getTask({
                taskId: context.taskId ?? '',
                processId: context.processId ?? ''
            })
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchTaskError'))
            throw error
        }
    }

    async fetchTaskDetailsById(
        taskId: number | string,
        processId: number | string
    ): Promise<AxiosResponse<ReportTaskDetailsResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getTask({
                taskId,
                processId
            })
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchTaskError'))
            throw error
        }
    }

    async fetchAdditionalActions(
        processId: number,
        productionSiteId: number
    ): Promise<AxiosResponse<AdditionalActionsResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getAdditionalActionProcessProductionSite({
                processId,
                productionSiteId
            })
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchAdditionalActionsError'))
            throw error
        }
    }

    async createReport(payload: CreateReportRequestPayloadSchema): Promise<AxiosResponse<CreateReportResponseSchema>> {
        try {
            return await reportExecutionQueries.createReport(payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.createReportError'))
            throw error
        }
    }

    async fetchCodeCompliances(queryString = ''): Promise<AxiosResponse<CodeCompliancesResponseSchema>> {
        try {
            return await reportExecutionQueries.getCodeCompliances(queryString)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchCodeCompliancesError'))
            throw error
        }
    }

    async createCodeCompliance(
        payload: CreateCodeCompliancePayloadSchema
    ): Promise<AxiosResponse<CreateCodeComplianceResponseSchema>> {
        try {
            return await reportExecutionQueries.createCodeCompliance(payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.createCodeComplianceError'))
            throw error
        }
    }

    async addCodeCompliancePattern(
        payload: AddCodeCompliancePatternPayloadSchema
    ): Promise<AxiosResponse<AddCodeCompliancePatternResponseSchema>> {
        try {
            return await reportExecutionQueries.addCodeCompliancePattern(payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.addCodeCompliancePatternError'))
            throw error
        }
    }

    async finishReport(
        reportId: number | string,
        payload: FinishReportPayloadSchema
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        try {
            return await reportExecutionQueries.finishReport(reportId, payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.finishReportError'))
            throw error
        }
    }

    async breakReport(
        reportId: number | string,
        payload: BreakReportPayloadSchema
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        try {
            return await reportExecutionQueries.updateReport(reportId, payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.breakReportError'))
            throw error
        }
    }

    async updateReport(
        reportId: number | string,
        payload: UpdateReportPayloadSchema
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        try {
            return await reportExecutionQueries.updateReport(reportId, payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.updateReportAmountError'))
            throw error
        }
    }

    async renewReport(
        reportId: number | string,
        payload: RenewReportPayloadSchema
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        try {
            return await reportExecutionQueries.renewReport(reportId, payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.renewReportError'))
            throw error
        }
    }

    async endMakeReady(
        reportId: number | string,
        payload: EndMakeReadyPayloadSchema
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        try {
            return await reportExecutionQueries.endMakeReady(reportId, payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.endMakeReadyError'))
            throw error
        }
    }

    async fetchBlackBoxAmount(payload: BlackBoxAmountPayloadSchema): Promise<AxiosResponse<BlackBoxAmountResponseSchema>> {
        try {
            return await reportExecutionQueries.getBlackBoxAmount(payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchBlackBoxAmountError'))
            throw error
        }
    }

    async setBlackBoxLight(payload: BlackBoxLightPayloadSchema): Promise<AxiosResponse<unknown>> {
        try {
            return await reportExecutionQueries.setBlackBoxLight(payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.setBlackBoxLightError'))
            throw error
        }
    }

    async pauseResumeBlackBoxCounting(
        payload: BlackBoxCounterSwitchPayloadSchema
    ): Promise<AxiosResponse<BlackBoxCounterSwitchResponseSchema>> {
        try {
            return await reportExecutionQueries.pauseResumeBlackBoxCounting(payload.reportId, payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.pauseResumeBlackBoxCountingError'))
            throw error
        }
    }

    async fetchPrinectAmount(payload: PrinectAmountPayloadSchema): Promise<AxiosResponse<PrinectAmountResponseSchema>> {
        try {
            const response = await reportExecutionQueries.getPrinectAmount(payload)
            notificationService.success(t('reports.reportExecution.service.prinectAmountSuccess'))

            return response
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchPrinectAmountError'))
            throw error
        }
    }

    async fetchPrinectMakeReadyAmount(
        payload: PrinectAmountPayloadSchema
    ): Promise<AxiosResponse<PrinectMakeReadyAmountResponseSchema>> {
        try {
            const response = await reportExecutionQueries.getPrinectMakeReadyAmount(payload)
            notificationService.success(t('reports.reportExecution.service.prinectMakeReadyAmountSuccess'))

            return response
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchPrinectMakeReadyAmountError'))
            throw error
        }
    }

    async updateReportTaskPlate(
        reportTaskPlateId: number | string,
        payload: UpdateReportTaskPlatePayloadSchema
    ): Promise<AxiosResponse<UpdateReportTaskPlateResponseSchema>> {
        try {
            return await reportExecutionQueries.updateReportTaskPlate(reportTaskPlateId, payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.updateReportItemPlateError'))
            throw error
        }
    }

    async fetchCommentCodes(
        codeTypeId: number | string,
        productionSiteId: number | string
    ): Promise<AxiosResponse<ReportCommentCodesResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getCommentCodes({
                codeTypeId,
                productionSiteId
            })
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchCommentCodesError'))
            throw error
        }
    }

    async fetchNextPlatesToReport(
        taskNumber: string | number,
        processId: number | string,
        typeId: number | string
    ): Promise<AxiosResponse<NextPlatesToReportResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getNextPlatesToReport({
                taskNumber,
                processId,
                typeId
            })
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchNextPlatesError'))
            throw error
        }
    }

    async fetchNextPlateToReport(
        processId: number | string,
        productionSiteId: number | string,
        previouslyReportedPrimaryNumber?: string | number | null
    ): Promise<AxiosResponse<NextPlateToReportResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getNextPlateToReport({
                processId,
                productionSiteId,
                previouslyReportedPrimaryNumber
            })
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchNextPlateError'))
            throw error
        }
    }

    async fetchRuntimeAdditionalActions(
        report: ActiveReportDetail
    ): Promise<AxiosResponse<AdditionalActionsResponseSchema> | null> {
        const request = buildRuntimeAdditionalActionsRequest(report)

        if (!request) {
            return null
        }

        try {
            if (request.kind === 'process-code') {
                return await reportTaskDiscoveryQueries.getAdditionalActionReportProcessCodeProductionSite(request.payload)
            }

            return await reportTaskDiscoveryQueries.getAdditionalActionProcessProductionSite(request.payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchAdditionalActionsError'))
            throw error
        }
    }

    async fetchReportItemColours(reportItemId: number | string): Promise<AxiosResponse<ReportItemColoursResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getReportItemColours(reportItemId)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchReportItemColoursError'))
            throw error
        }
    }

    async fetchPlateOrderReasons(): Promise<AxiosResponse<PlateOrderReasonsResponseSchema>> {
        try {
            return await reportPlateOrdersQueries.getPlateOrderReasons()
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchPlateOrderReasonsError'))
            throw error
        }
    }

    async createPlateOrder(payload: CreatePlateOrderPayload): Promise<AxiosResponse<unknown>> {
        try {
            const response = await reportPlateOrdersQueries.createPlateOrder(payload)

            return response
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.createPlateOrderError'))
            throw error
        }
    }

    async fetchPlateOrderHistory(params: PlateOrderHistoryParams): Promise<AxiosResponse<PlateOrderHistoryResponseSchema>> {
        try {
            return await reportPlateOrdersQueries.getPlateOrderHistory(params)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchPlateOrderHistoryError'))
            throw error
        }
    }

    async fetchMachinePlateItems(
        machineCode: string,
        limit: number,
        offset: number
    ): Promise<AxiosResponse<MachinePlateItemsResponseSchema>> {
        try {
            return await reportPlateOrdersQueries.getMachinePlateItems(machineCode, limit, offset)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchMachinePlateItemsError'))
            throw error
        }
    }

    async checkPalletParameter(parameter: string): Promise<AxiosResponse<CheckPalletParameterResponseSchema>> {
        try {
            return await paperReportingQueries.checkPalletParameter(parameter)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.checkPalletParameterError'))
            throw error
        }
    }

    async addPallet(payload: AddReportPalletPayloadSchema): Promise<AxiosResponse<AddReportPalletResponseSchema>> {
        try {
            return await paperReportingQueries.addPallet(payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.addPalletError'))
            throw error
        }
    }

    async removePallet(reportPalletId: number | string): Promise<AxiosResponse<unknown>> {
        try {
            return await paperReportingQueries.removePallet(reportPalletId)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.removePalletError'))
            throw error
        }
    }

    async fetchReportMaterialTypes(): Promise<AxiosResponse<ReportMaterialTypesResponseSchema>> {
        try {
            return await reportMaterialsQueries.getReportMaterialTypes()
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchMaterialTypesError'))
            throw error
        }
    }

    async fetchReportMaterials(materialTypeId?: number | null): Promise<AxiosResponse<ReportMaterialsResponseSchema>> {
        try {
            const response = await reportMaterialsQueries.getReportMaterials(materialTypeId)

            return {
                ...response,
                data: {
                    ...response.data,
                    results: response.data.results.map((material) => this.normalizeReportMaterial(material))
                }
            }
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.fetchMaterialsError'))
            throw error
        }
    }

    async createReportMaterialReservation(payload: ReportMaterialReservationPayloadSchema): Promise<AxiosResponse<unknown>> {
        try {
            return await reportMaterialsQueries.createReportMaterialReservation(payload)
        } catch (error) {
            handleApiError(error, t('reports.reportExecution.service.createMaterialReservationError'))
            throw error
        }
    }

    buildCreateReportPayload(input: CreateReportPayloadInput): CreateReportPayloadSchema {
        return {
            processCodeId: 1,
            objectId: input.plate.id,
            objectType: input.plate.objectType,
            objectNumber: this.getTaskNumber(input.task),
            plateNumber: input.plate.plateNumber || 1,
            machine: input.workspace.machine?.id ?? null,
            process: input.context.processId ?? input.task.productionProcessId ?? 0,
            user: input.userId,
            outOfSequenceCodeId: input.context.outOfSequenceCodeId,
            plannedDuration: input.plate.duration ?? null,
            currentlyReportedQuantity: 0,
            productionSiteId: input.workspace.id,
            additionalActionProcessProductionSites: input.additionalActions.map((action) => ({ ...action })),
            startMakeReady: input.workspace.makeReadyRequired === true,
            reportPalletsData: input.reportPalletsData.map((pallet) => ({ ...pallet }))
        }
    }

    buildCreateProcessReportPayload(input: {
        processCodeId: number
        machine: number | null
        userId: number | null
        productionSiteId: number
    }): CreateProcessReportPayloadSchema {
        return {
            processCodeId: input.processCodeId,
            objectType: REPORTS.REPORT_OBJECT_TYPES.OTHER_TASK,
            objectId: input.processCodeId,
            objectNumber: '-',
            machine: input.machine,
            user: input.userId,
            currentlyReportedQuantity: 0,
            productionSiteId: input.productionSiteId
        }
    }

    getTaskNumber(task: ReportTaskDetails | null): string {
        const rawTaskNumber = task?.primaryNumber ?? task?.procNumber ?? task?.taskNumber ?? task?.order ?? ''

        return String(rawTaskNumber)
    }

    findPlate(task: ReportTaskDetails, plateNumber: string | null): ReportTaskPlate | null {
        const plates = this.getReportablePlates(task)

        if (plateNumber) {
            const matchingPlate = plates.find((plate) => String(plate.plateNumber ?? '') === plateNumber)

            if (matchingPlate) {
                return matchingPlate
            }
        }

        return plates[0] ?? null
    }

    findPrintEntryPlate(task: ReportTaskDetails, plateNumber: string | null): ReportTaskPlate | null {
        const mountPlateNumber = plateNumber ?? this.getFirstReportablePrintPlateNumber(task)

        if (mountPlateNumber === null) {
            return null
        }

        if (this.hasFullyReportedABPlate(task, mountPlateNumber)) {
            return null
        }

        return this.findPrintPlateByPriority(task, mountPlateNumber)
    }

    findPrintPlateByPriority(task: ReportTaskDetails, plateNumber: string | number): ReportTaskPlate | null {
        for (const side of REPORTS.CREATE_PRINT_REPORT.SIDE_PRIORITY) {
            const plate = this.findPrintPlate(task, side, plateNumber)

            if (plate) {
                return plate
            }
        }

        return null
    }

    findPrintPlate(task: ReportTaskDetails, side: ReportTaskPlateSideType, plateNumber: string | number): ReportTaskPlate | null {
        const normalizedPlateNumber = String(plateNumber)

        return (
            task.reportItemPlates.find((plate) => {
                if (String(plate.plateNumber ?? '') !== normalizedPlateNumber || plate.printSideType !== side) {
                    return false
                }

                if (side === REPORTS.CREATE_PRINT_REPORT.SIDE_TYPES.AB) {
                    return plate.emergencyMode === false && plate.isFullReported !== true
                }

                return plate.isFullReported !== true
            }) ?? null
        )
    }

    getPrintMountPlates(task: ReportTaskDetails, plateNumber: string | number | null): ReportTaskPlate[] {
        const normalizedPlateNumber = plateNumber !== null && plateNumber !== undefined ? String(plateNumber) : null
        const currentMountPlates = task.reportItemPlates.filter((plate) => {
            if (!normalizedPlateNumber) {
                return true
            }

            return String(plate.plateNumber ?? '') === normalizedPlateNumber
        })

        if (
            currentMountPlates.some(
                (plate) => plate.printSideType === REPORTS.CREATE_PRINT_REPORT.SIDE_TYPES.AB && plate.emergencyMode === false
            )
        ) {
            return currentMountPlates.filter((plate) => plate.printSideType === REPORTS.CREATE_PRINT_REPORT.SIDE_TYPES.AB)
        }

        return currentMountPlates.filter((plate) => plate.printSideType !== REPORTS.CREATE_PRINT_REPORT.SIDE_TYPES.AB)
    }

    getPrintModeType(
        plates: ReportTaskPlate[]
    ): (typeof REPORTS.CREATE_PRINT_REPORT.MODE_TYPES)[keyof typeof REPORTS.CREATE_PRINT_REPORT.MODE_TYPES] {
        if (plates.some((plate) => plate.printSideType === REPORTS.CREATE_PRINT_REPORT.SIDE_TYPES.AB)) {
            return REPORTS.CREATE_PRINT_REPORT.MODE_TYPES.DOUBLE_SW
        }

        return plates.length > 1 ? REPORTS.CREATE_PRINT_REPORT.MODE_TYPES.DOUBLE : REPORTS.CREATE_PRINT_REPORT.MODE_TYPES.SINGLE
    }

    getFirstReportablePrintPlateNumber(task: ReportTaskDetails): string | null {
        const plate = task.reportItemPlates.find((item) => {
            if (item.printSideType === REPORTS.CREATE_PRINT_REPORT.SIDE_TYPES.AB) {
                return item.emergencyMode === false && item.isFullReported !== true
            }

            return item.isFullReported !== true
        })

        if (plate?.plateNumber === null || plate?.plateNumber === undefined) {
            return null
        }

        return String(plate.plateNumber)
    }

    hasFullyReportedABPlate(task: ReportTaskDetails, plateNumber: string | number): boolean {
        const normalizedPlateNumber = String(plateNumber)

        return task.reportItemPlates.some((plate) => {
            return (
                String(plate.plateNumber ?? '') === normalizedPlateNumber &&
                plate.printSideType === REPORTS.CREATE_PRINT_REPORT.SIDE_TYPES.AB &&
                plate.isFullReported === true
            )
        })
    }

    getReportablePlates(task: ReportTaskDetails): ReportTaskPlate[] {
        const plates = task.reportItemPlates.filter((plate) => plate.isFullReported !== true)

        if (task.productionProcessId === REPORTS.CREATE_REPORT.CUT_OUT_ELASTIC_PROCESS_ID) {
            return plates.filter((plate) => plate.hasNewElastic === true)
        }

        return plates
    }

    mapAdditionalActions(payload: AdditionalActionsResponseSchema | null | undefined): ReportAdditionalAction[] {
        return (payload?.results || []).map((action) => ({
            ...action,
            isSelected: action.isSelected ?? true
        }))
    }

    normalizeReportMaterial(material: ReportMaterialApi): ReportMaterial {
        return {
            id: material.id,
            name: material.name,
            materialTypeId: material.materialTypeId,
            unitOfMeasure: this.normalizeReportMaterialUnitOfMeasure(material)
        }
    }

    normalizeReportMaterialUnitOfMeasure(material: ReportMaterialApi): ReportMaterial['unitOfMeasure'] {
        const unitOfMeasure = material.unitOfMeasure || material.unit_of_measure || material.unit || material.uom || null

        if (typeof unitOfMeasure === 'string') {
            return {
                name: null,
                symbol: unitOfMeasure,
                label: unitOfMeasure
            }
        }

        const name = unitOfMeasure?.name || material.unitOfMeasureName || material.unitName || null
        const symbol = unitOfMeasure?.symbol || null
        const label = name || symbol || ''

        if (!label) {
            return null
        }

        return {
            name,
            symbol,
            label
        }
    }
}

export const reportExecutionService = new ReportExecutionService()
